import React, { useEffect, useState } from "react";
import {
  Modal,
  Table,
  Button,
  message,
  Tooltip,
  Space,
  Tag,
  Typography,
  Popconfirm,
  Alert,
  Empty,
  Tabs,
} from "antd";
import {
  ReloadOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  WarningOutlined,
  DisconnectOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "../../css/modal-list-sync.css"; // Asegúrate de que los estilos estén correctamente definidos

const { Text } = Typography;
const { TabPane } = Tabs;

// Interfaz para los elementos en cola
interface QueuedItem {
  id: string;
  url: string;
  timestamp: number;
  status: string;
  attempts: number;
  method: string;
  data?: any;
}

export const SyncQueueManager: React.FC<{
  visible: boolean;
  onClose: () => void;
  onSync?: () => void;
}> = ({ visible, onClose, onSync }) => {
  const [queuedItems, setQueuedItems] = useState<QueuedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedItem, setSelectedItem] = useState<QueuedItem | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [activeTab, setActiveTab] = useState("posts");

  // Manejar cambios en el estado de conexión
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Verificar elementos pendientes cuando se recupera la conexión
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Escuchar mensajes del Service Worker
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "QUEUE_ITEMS") {
        const items = event.data.items.map(normalizeQueueItem);
        setQueuedItems(items);
        setLoading(false);
        if (items.length === 0 && !errorMessage) {
          setErrorMessage("No hay elementos en la cola de sincronización.");
        }
      } else if (event.data && event.data.type === "QUEUE_ERROR") {
        setErrorMessage(event.data.message || "Error al obtener la cola.");
        setQueuedItems([]);
        setLoading(false);
      } else if (event.data && event.data.type === "PENDING_SYNC_AVAILABLE") {
        // Recibimos notificación de elementos pendientes para sincronizar
        if (visible) {
          // Si el modal está visible, recargar datos
          if (event.data.hasPendingPosts) {
            loadQueuedItems();
          }
        } else if (onSync) {
          // Si el modal no está visible, llamar al callback para actualizar UI externa
          onSync();
        }
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [errorMessage, visible, onSync]);

  // Cargar elementos de la cola al abrir el modal
  useEffect(() => {
    if (visible) {
      loadQueuedItems();
    }
  }, [visible]);

  const getQueuedItemsFromBothSources = async (): Promise<QueuedItem[]> => {
    const items: QueuedItem[] = [];

    // ✅ PRIMERO: Intentar simple-queue-storage (principal)
    try {
      const simpleQueueItems = await getQueuedItemsFromDB(
        "simple-queue-storage",
        "requests"
      );
      items.push(...simpleQueueItems);
      console.log(
        `📱 Encontrados ${simpleQueueItems.length} elementos en simple-queue-storage`
      );
    } catch (error) {
      console.warn("⚠️ Error accediendo simple-queue-storage:", error);
    }

    return items;
  };

  // 5. FUNCIÓN HELPER GENÉRICA
  const getQueuedItemsFromDB = async (
    dbName: string,
    storeName: string
  ): Promise<QueuedItem[]> => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName);

      request.onerror = () => reject(new Error(`Error abriendo ${dbName}`));

      request.onsuccess = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains(storeName)) {
          db.close();
          resolve([]);
          return;
        }

        try {
          const transaction = db.transaction([storeName], "readonly");
          const store = transaction.objectStore(storeName);
          const getAllRequest = store.getAll();

          getAllRequest.onsuccess = () => {
            const items = getAllRequest.result || [];
            db.close();
            resolve(items.map(normalizeQueueItem));
          };

          getAllRequest.onerror = () => {
            db.close();
            resolve([]);
          };
        } catch (error) {
          db.close();
          resolve([]);
        }
      };
    });
  };

  const loadQueuedItems = async () => {
    setLoading(true);
    setErrorMessage(null);

    // ✅ PRIMERO: Intentar obtener del Service Worker
    if (navigator.serviceWorker.controller) {
      const messageChannel = new MessageChannel();

      messageChannel.port1.onmessage = (event) => {
        if (event.data && event.data.type === "QUEUE_ITEMS") {
          const items = event.data.items.map(normalizeQueueItem);
          setQueuedItems(items);

          if (items.length === 0) {
            setErrorMessage("No hay elementos en la cola de sincronización.");
          }
          setLoading(false);
        } else if (event.data && event.data.type === "QUEUE_ERROR") {
          setErrorMessage(event.data.message || "Error al obtener la cola.");
          setQueuedItems([]);

          // ✅ FALLBACK MEJORADO: Intentar ambas DBs
          getQueuedItemsFromBothSources()
            .then((items) => {
              setQueuedItems(items);
              if (items.length === 0) {
                setErrorMessage(
                  "No hay elementos en la cola de sincronización."
                );
              } else {
                setErrorMessage(null); // Limpiar error si encontramos datos
              }
            })
            .catch((error) => {
              console.error("Error en fallback:", error);
              setErrorMessage(
                "No se pudieron cargar los elementos de ninguna base de datos."
              );
            })
            .finally(() => {
              setLoading(false);
            });
          return; // ✅ IMPORTANTE: No continuar si hay error
        }
      };

      navigator.serviceWorker.controller.postMessage(
        { type: "GET_QUEUE_ITEMS" },
        [messageChannel.port2]
      );
    } else {
      // ✅ FALLBACK: Si no hay SW, usar ambas DBs
      try {
        const items = await getQueuedItemsFromBothSources();
        setQueuedItems(items);
        if (items.length === 0) {
          setErrorMessage("No hay elementos en la cola de sincronización.");
        }
      } catch (error) {
        console.error("Error al cargar elementos en cola:", error);
        setErrorMessage(
          "No se pudieron cargar los elementos en cola: " +
            (error instanceof Error ? error.message : "Error desconocido")
        );
        setQueuedItems([]);
      } finally {
        setLoading(false);
      }
    }
  };

  // Función para normalizar la estructura de los elementos de la cola
  const normalizeQueueItem = (item: any): QueuedItem => {
    // ✅ ADAPTADO: Para el formato que usa simple-queue-storage
    let url = item.url || "URL no disponible";
    let method = item.method || "POST";
    let data = null;

    // simple-queue-storage guarda el body como objeto/string directamente
    if (item.body) {
      try {
        if (typeof item.body === "string") {
          data = JSON.parse(item.body);
        } else {
          data = item.body;
        }
      } catch (e) {
        data = item.body;
      }
    }

    return {
      id:
        item.id ||
        `id-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      url,
      timestamp: item.timestamp || Date.now(),
      status: item.status || "pending",
      attempts: item.attempts || 0,
      method,
      data,
    };
  };

  // Intentar reenviar un elemento específico
  const handleRetryItem = async (id: string) => {
    if (!navigator.serviceWorker.controller) {
      message.error("Service Worker no está disponible");
      return;
    }

    if (!isOnline) {
      message.warning(
        "No hay conexión a internet. Intenta nuevamente cuando estés online."
      );
      return;
    }

    setLoading(true);
    try {
      navigator.serviceWorker.controller.postMessage({
        type: "RETRY_SPECIFIC_ITEM",
        id,
      });
      message.loading("Reenviando solicitud...");
      setTimeout(() => {
        loadQueuedItems();
        if (onSync) onSync();
      }, 2000);
    } catch (error) {
      console.error("Error al reenviar elemento:", error);
      message.error("Error al reenviar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  // Eliminar un elemento de la cola
  const handleDeleteItem = async (id: string) => {
    setLoading(true);
    try {
      await deleteQueuedItem(id);
      message.success("Elemento eliminado de la cola");
      loadQueuedItems();
      if (onSync) onSync();
    } catch (error) {
      console.error("Error al eliminar elemento:", error);
      message.error("No se pudo eliminar el elemento");
    } finally {
      setLoading(false);
    }
  };

  const deleteQueuedItem = async (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const dbName = "simple-queue-storage";
      const storeName = "requests";

      const request = indexedDB.open(dbName);

      request.onerror = (event) => {
        console.error(`Error al abrir ${dbName}:`, event);
        reject(new Error(`No se pudo abrir la base de datos`));
      };

      request.onsuccess = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(storeName)) {
          if (db.objectStoreNames.contains("requests")) {
            deleteItemFromStore(db, "requests", id, resolve, reject);
          } else {
            db.close();
            reject(new Error(`Almacén ${storeName} no encontrado`));
          }
          return;
        }
        deleteItemFromStore(db, storeName, id, resolve, reject);
      };
    });
  };

  const deleteItemFromStore = (
    db: IDBDatabase,
    storeName: string,
    id: string,
    resolve: () => void,
    reject: (error: Error) => void
  ) => {
    try {
      const transaction = db.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const deleteRequest = store.delete(id);

      deleteRequest.onsuccess = () => {
        db.close();
        resolve();
      };

      deleteRequest.onerror = (event) => {
        console.error(`Error al eliminar de ${storeName}:`, event);
        db.close();
        reject(new Error(`Error al eliminar elemento`));
      };

      transaction.oncomplete = () => db.close();
      transaction.onerror = (event) => {
        console.error(`Error en transacción de eliminación:`, event);
        db.close();
        reject(new Error(`Error en transacción`));
      };
    } catch (error) {
      console.error(`Error al acceder a ${storeName}:`, error);
      db.close();
      reject(new Error(`Error inesperado al eliminar`));
    }
  };

  const showItemDetails = (item: QueuedItem) => {
    setSelectedItem(item);
    setDetailsVisible(true);
  };

  // Columnas para la tabla de solicitudes POST
  const columns = [
    {
      title: "Acciones",
      key: "actions",
      render: (_: any, record: QueuedItem) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              icon={<InfoCircleOutlined />}
              size="small"
              onClick={() => showItemDetails(record)}
            />
          </Tooltip>
          <Tooltip
            title={isOnline ? "Reintentar" : "Necesitas conexión a internet"}
          >
            <Button
              icon={<ReloadOutlined />}
              size="small"
              type="primary"
              disabled={!isOnline}
              onClick={() => handleRetryItem(record.id)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Popconfirm
              title="¿Eliminar solicitud?"
              description="Esta solicitud se eliminará permanentemente"
              onConfirm={() => handleDeleteItem(record.id)}
              okText="Sí"
              cancelText="No"
            >
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Endpoint",
      dataIndex: "url",
      key: "url",
      render: (url: string) => {
        try {
          if (
            url.includes("/workflow-container") ||
            url.includes("/api/workflow")
          ) {
            const parts = url.split("/").filter((part) => part.trim() !== "");
            const endpoint = parts[parts.length - 1] || "desconocido";
            const endpointMap: Record<string, string> = {
              "workflow-container-checkpoint": "Checkpoint",
              "workflow-container-empty": "Container Vacío",
              "workflow-container-full": "Container Completo",
              "workflow-truck": "Camión",
            };
            const colorMap: Record<string, string> = {
              checkpoint: "blue",
              empty: "orange",
              full: "green",
              truck: "cyan",
            };
            let displayName = endpoint;
            let color = "default";
            for (const [key, value] of Object.entries(endpointMap)) {
              if (endpoint.includes(key)) {
                displayName = value;
                for (const [typeKey, typeColor] of Object.entries(colorMap)) {
                  if (key.includes(typeKey)) {
                    color = typeColor;
                    break;
                  }
                }
                break;
              }
            }
            return (
              <Tooltip title={url}>
                <Tag color={color}>{displayName}</Tag>
              </Tooltip>
            );
          }
          const parts = url.split("/").filter((part) => part.trim() !== "");
          const endpoint = parts[parts.length - 1] || "desconocido";
          return (
            <Tooltip title={url}>
              <Text>{endpoint}</Text>
            </Tooltip>
          );
        } catch (error) {
          console.error("Error al procesar URL:", error);
          return <Text>Error en URL</Text>;
        }
      },
    },
    {
      title: "Fecha",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp: number) => (
        <Tooltip title={dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss")}>
          <Text>{dayjs(timestamp).format("DD/MM/YYYY HH:mm")}</Text>
        </Tooltip>
      ),
      responsive: ["sm"],
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: QueuedItem) => {
        let color = "blue";
        let text = "Pendiente";
        let icon = null;
        switch (status) {
          case "pending":
            color = "blue";
            text = "Pendiente";
            icon = <ClockCircleOutlined />;
            break;
          case "in_progress":
            color = "orange";
            text = "En proceso";
            icon = <SyncOutlined spin />;
            break;
          case "failed":
            color = "red";
            text = `Fallido (${record.attempts || 0})`;
            icon = <WarningOutlined />;
            break;
          case "completed":
            color = "green";
            text = "Completado";
            icon = <InfoCircleOutlined />;
            break;
          default:
            color = "default";
            text = status || "Desconocido";
        }
        return (
          <Tag color={color} icon={icon}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Método",
      dataIndex: "method",
      key: "method",
      render: (method: string) => <Tag color="purple">{method}</Tag>,
      responsive: ["lg"],
    },
  ] as any[];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) return;

    setLoading(true);
    try {
      for (const key of selectedRowKeys) {
        await deleteQueuedItem(key.toString());
      }
      message.success(`${selectedRowKeys.length} elementos eliminados`);
      setSelectedRowKeys([]);
      loadQueuedItems();
      if (onSync) onSync();
    } catch (error) {
      console.error("Error al eliminar elementos:", error);
      message.error("Error al eliminar algunos elementos");
    } finally {
      setLoading(false);
    }
  };

  const handleProcessAll = () => {
    if (!navigator.serviceWorker.controller) {
      message.error("Service Worker no está disponible");
      return;
    }

    if (!isOnline) {
      message.warning(
        "No hay conexión a internet. La sincronización se realizará automáticamente cuando vuelvas a estar online."
      );
      return;
    }

    navigator.serviceWorker.controller.postMessage({
      type: "SYNC_NOW",
    });
    message.loading("Procesando solicitudes pendientes...");
    setTimeout(() => {
      loadQueuedItems();
      if (onSync) onSync();
    }, 2000);
  };

  return (
    <>
      <Modal
        title={
          <div>
            <ClockCircleOutlined style={{ marginRight: 8 }} />
            Gestor de Cola de Sincronización
            {queuedItems.length > 0 && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {queuedItems.length} elemento(s)
              </Tag>
            )}
            <Tag
              color={isOnline ? "green" : "red"}
              icon={isOnline ? <WifiOutlined /> : <DisconnectOutlined />}
              style={{ marginLeft: 8 }}
            >
              {isOnline ? "Online" : "Offline"}
            </Tag>
          </div>
        }
        open={visible}
        onCancel={onClose}
        width="90vw"
        footer={[
          <Button
            key="refresh"
            onClick={() => {
              loadQueuedItems();
            }}
            icon={<ReloadOutlined />}
          >
            Refrescar
          </Button>,
          activeTab === "posts" ? (
            <Button
              key="process-all"
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleProcessAll}
              disabled={!isOnline || queuedItems.length === 0}
            >
              Procesar Todo
            </Button>
          ) : null,
          activeTab === "posts" && (
            <Button
              key="delete-selected"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDeleteSelected}
              disabled={selectedRowKeys.length === 0}
            >
              Eliminar ({selectedRowKeys.length})
            </Button>
          ),
          <Button key="close" onClick={onClose}>
            Cerrar
          </Button>,
        ]}
        destroyOnClose
      >
        {!isOnline && (
          <Alert
            message="Modo offline activo"
            description="No hay conexión a internet. Puedes ver y eliminar solicitudes pendientes, pero la sincronización requiere conexión."
            type="warning"
            showIcon
            icon={<DisconnectOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ marginBottom: 16 }}
        >
          <TabPane
            tab={
              <span>
                <ClockCircleOutlined />
                &nbsp; Solicitudes POST
                {queuedItems.length > 0 && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {queuedItems.length}
                  </Tag>
                )}
              </span>
            }
            key="posts"
          >
            {errorMessage && (
              <Alert
                message="Información"
                description={errorMessage}
                type={queuedItems.length === 0 ? "info" : "error"}
                showIcon
                closable
                onClose={() => setErrorMessage(null)}
                style={{ marginBottom: 16 }}
              />
            )}

            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={queuedItems.map((item) => ({
                ...item,
                key: item.id,
              }))}
              loading={loading}
              pagination={{ pageSize: 10 }}
              size="small"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      loading
                        ? "Cargando elementos..."
                        : errorMessage ||
                          "No hay elementos en la cola de sincronización"
                    }
                  />
                ),
              }}
              scroll={{ x: "max-content" }}
            />
          </TabPane>
        </Tabs>
      </Modal>

      <Modal
        title={
          <div>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            Detalles de la Solicitud
            {selectedItem?.method && (
              <Tag color="purple" style={{ marginLeft: 8 }}>
                {selectedItem.method}
              </Tag>
            )}
          </div>
        }
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            Cerrar
          </Button>,
        ]}
        width={700}
        destroyOnClose
      >
        {selectedItem && (
          <div>
            <h3>Información General</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
              }}
            >
              <div>
                <strong>ID:</strong> {selectedItem.id}
              </div>
              <div>
                <strong>Estado:</strong>{" "}
                <Tag
                  color={
                    selectedItem.status === "pending"
                      ? "blue"
                      : selectedItem.status === "in_progress"
                      ? "orange"
                      : selectedItem.status === "failed"
                      ? "red"
                      : selectedItem.status === "completed"
                      ? "green"
                      : "default"
                  }
                >
                  {selectedItem.status === "pending"
                    ? "Pendiente"
                    : selectedItem.status === "in_progress"
                    ? "En Progreso"
                    : selectedItem.status === "failed"
                    ? "Fallido"
                    : selectedItem.status === "completed"
                    ? "Completado"
                    : selectedItem.status}
                </Tag>
              </div>
              <div>
                <strong>Fecha:</strong>{" "}
                {selectedItem.timestamp
                  ? dayjs(selectedItem.timestamp).format("DD/MM/YYYY HH:mm:ss")
                  : "Desconocida"}
              </div>
              <div>
                <strong>Intentos:</strong> {selectedItem.attempts || 0}
              </div>
            </div>

            <h3 style={{ marginTop: 16 }}>Información de Endpoint</h3>
            <div>
              <strong>URL Completa:</strong> {selectedItem.url}
            </div>
            {selectedItem.url.includes("workflow-container") && (
              <div style={{ marginTop: 8 }}>
                <strong>Tipo de Flujo:</strong>{" "}
                {selectedItem.url.includes("checkpoint") ? (
                  <Tag color="blue">Checkpoint</Tag>
                ) : selectedItem.url.includes("empty") ? (
                  <Tag color="orange">Container Vacío</Tag>
                ) : selectedItem.url.includes("full") ? (
                  <Tag color="green">Container Completo</Tag>
                ) : selectedItem.url.includes("truck") ? (
                  <Tag color="cyan">Camión</Tag>
                ) : (
                  <Tag>Desconocido</Tag>
                )}
              </div>
            )}

            {selectedItem.data && (
              <>
                <h3 style={{ marginTop: 16 }}>Datos de la Solicitud</h3>
                {typeof selectedItem.data === "object" &&
                  selectedItem.data !== null && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                      }}
                    >
                      {selectedItem.data.containerId && (
                        <div>
                          <strong>ID de Contenedor:</strong>{" "}
                          {selectedItem.data.containerId}
                        </div>
                      )}
                      {selectedItem.data.truckId && (
                        <div>
                          <strong>ID de Camión:</strong>{" "}
                          {selectedItem.data.truckId}
                        </div>
                      )}
                      {selectedItem.data.checkpointId && (
                        <div>
                          <strong>ID de Checkpoint:</strong>{" "}
                          {selectedItem.data.checkpointId}
                        </div>
                      )}
                      {selectedItem.data.clientId && (
                        <div>
                          <strong>ID de Cliente:</strong>{" "}
                          {selectedItem.data.clientId}
                        </div>
                      )}
                    </div>
                  )}
                <div style={{ marginTop: 16 }}>
                  <strong>Datos Completos:</strong>
                  <pre
                    style={{
                      background: "#f5f5f5",
                      padding: 16,
                      borderRadius: 4,
                    }}
                  >
                    {typeof selectedItem.data === "object"
                      ? JSON.stringify(selectedItem.data, null, 2)
                      : String(selectedItem.data)}
                  </pre>
                </div>
              </>
            )}

            <div style={{ marginTop: 16 }}>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  type="primary"
                  disabled={!isOnline}
                  onClick={() => {
                    handleRetryItem(selectedItem.id);
                    setDetailsVisible(false);
                  }}
                >
                  Reintentar Envío
                </Button>
                <Popconfirm
                  title="¿Eliminar solicitud?"
                  description="Esta solicitud se eliminará permanentemente"
                  onConfirm={() => {
                    handleDeleteItem(selectedItem.id);
                    setDetailsVisible(false);
                  }}
                  okText="Sí"
                  cancelText="No"
                >
                  <Button icon={<DeleteOutlined />} danger>
                    Eliminar
                  </Button>
                </Popconfirm>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export const SyncQueueButton: React.FC<{
  pendingCount?: number;
}> = ({ pendingCount = 0 }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [count, setCount] = useState(pendingCount);

  useEffect(() => {
    const checkPendingItems = async () => {
      if (!navigator.serviceWorker.controller) return;

      try {
        const messageChannel = new MessageChannel();

        const pendingPosts = await new Promise<boolean>((resolve) => {
          messageChannel.port1.onmessage = (event) => {
            if (event.data && event.data.type === "PENDING_ITEMS_RESULT") {
              resolve(event.data.hasPending);
            } else {
              resolve(false);
            }
          };

          // Establecer un timeout por si no hay respuesta
          setTimeout(() => resolve(false), 3000);

          navigator!.serviceWorker!.controller!.postMessage(
            {
              type: "CHECK_PENDING_ITEMS",
            },
            [messageChannel.port2]
          );
        });

        // Actualizar contador
        if (pendingPosts) {
          // Contar elementos pendientes
          let totalPending = 0;

          if (totalPending > 0 || pendingPosts) {
            setCount(totalPending > 0 ? totalPending : pendingCount);
          }
        }
      } catch (error) {
        console.error("Error al verificar elementos pendientes:", error);
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "PENDING_SYNC_AVAILABLE") {
        // Actualizar UI cuando se recibe notificación de elementos pendientes
        checkPendingItems();
      }
    };

    const handleOnline = () => {
      setIsOnline(true);
      setTimeout(() => {
        checkPendingItems();
      }, 1000);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    navigator.serviceWorker.addEventListener("message", handleMessage);

    // Verificar al montar el componente
    checkPendingItems();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [pendingCount]);

  return (
    <>
      <Button
        type="primary"
        icon={<ClockCircleOutlined />}
        onClick={() => setModalVisible(true)}
      >
        Cola de Sincronización
        {count > 0 && (
          <Tag color="red" style={{ marginLeft: 8 }}>
            {count}
          </Tag>
        )}
        {!isOnline && (
          <Tooltip title="Sin conexión a internet">
            <DisconnectOutlined style={{ marginLeft: 5, color: "#ff4d4f" }} />
          </Tooltip>
        )}
      </Button>

      {modalVisible && (
        <ErrorBoundary
          fallback={
            <Modal
              title="Error al cargar el gestor de cola"
              open={true}
              onCancel={() => setModalVisible(false)}
              footer={[
                <Button key="close" onClick={() => setModalVisible(false)}>
                  Cerrar
                </Button>,
              ]}
              destroyOnClose
            >
              <Alert
                message="Error inesperado"
                description="Ocurrió un error al intentar cargar el gestor de cola. Esto puede deberse a problemas con la base de datos local o la conexión."
                type="error"
                showIcon
              />
              <div style={{ marginTop: 16 }}>
                <p>Sugerencias:</p>
                <ul>
                  <li>Verifica tu conexión a internet</li>
                  <li>Actualiza la página</li>
                  <li>Limpia la caché del navegador si el problema persiste</li>
                </ul>
              </div>
            </Modal>
          }
        >
          <SyncQueueManager
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
          />
        </ErrorBoundary>
      )}
    </>
  );
};

class ErrorBoundary extends React.Component<{
  children: React.ReactNode;
  fallback: React.ReactNode;
}> {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("Error en componente:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

export default SyncQueueManager;
